<template>
	<BuyersOrderGroup
		name="Funding"
		class="ignore-all-locks"
	>
		<template #right-icon>
			<div
				v-if="totalFundingInvalidMessage != null"
				class="error-text"
			>
				<TooltipComponent
					:tooltip="[totalFundingInvalidMessage]"
					direction="top"
					isError
				>
					<template #showFromOutside>
						<i class="fas fa-exclamation-circle"></i>
					</template>
				</TooltipComponent>
			</div>
			<span v-else>+</span>
		</template>
		<template #column-1>
			<InputCurrency
				:saturn="totalFunding"
				:disabled="true"
				:precision="2"
				iconSide="left"
				:negative="false"
				:invalid="totalFundingInvalidMessage != null"
			>
			</InputCurrency>
		</template>
		<template #column-2>
			<TooltipComponent
				:tooltip="['Sync Funding']"
				:direction="'right'"
				maxWidth="450"
			>
				<template #showFromOutside>
					<button
						v-if="fimenu.store.storeSettings.externalDMSSource == ExternalDMSSource.CDK"
						class="sync-funding-button"
						@click="onSyncFundingClick"
						:disabled="isBusySyncingFunding"
					>
						<i
							class="fas fa-sync"
							:class="{ 'fa-spin': isBusySyncingFunding }"
						></i>
					</button>
				</template>
			</TooltipComponent>
		</template>

		<template #children>
			<BuyersOrderRow
				v-for="funding in fimenu.fundings"
				:key="funding.name"
				indented
			>
				<template #info>
					<button
						class="button-link"
						@click="onAddEditItemClick(funding)"
					>
						{{ funding.name }}
					</button>
				</template>

				<template #right-icon>
					<span>+</span>
				</template>

				<template #column-1>
					<InputCurrency
						:saturn="funding.amount"
						:disabled="true"
						:precision="2"
						iconSide="left"
						:negative="false"
					/>
				</template>
			</BuyersOrderRow>
		</template>
	</BuyersOrderGroup>
</template>
<script setup lang="ts">
	import { computed, ref } from 'vue';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import BuyersOrderRow from './BuyersOrderRow.vue';
	import BuyersOrderGroup from './BuyersOrderGroup.vue';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuFunding from '@core/classes/FIMenuFunding';
	import api from '@core/services/api/_api';
	import util from '@core/services/util';
	import $modal from '@core/services/modal';
	import modalInfo from '@core/modals/modalInfo.vue';
	import TooltipComponent from '@core/components/TooltipComponent.vue';
	import { ExternalDMSSource } from '@core/classes/SharedEnums';

	interface IBuyersOrderFundingProps {
		fimenu: FIMenu;
	}

	const isBusySyncingFunding = ref(false);

	const { fimenu } = defineProps<IBuyersOrderFundingProps>();

	const emit = defineEmits<{
		//TODO: Add type for funding
		addEditFundingClick: [funding: FIMenuFunding];
	}>();

	function onAddEditItemClick(funding: FIMenuFunding = null) {
		emit('addEditFundingClick', funding);
	}

	async function onSyncFundingClick() {
		const companyId = fimenu.store.storeSettings.dywSettings.companyNumber;
		if (companyId === null) {
			util.toastr('error', 'Error', 'CDK Company Number is not set');
			return;
		}
		isBusySyncingFunding.value = true;
		const response = await api.fimenu.syncFunding(fimenu, companyId);

		if (response) {
			if (response.data.length === 0) {
				util.toastr('success', 'Success', 'No transactions found');
			} else {
				util.toastr('success', 'Success', 'Funding synced successfully');
				fimenu.fundings = response.data;
			}
		} else {
			util.toastr('error', 'Error', 'Failed to sync funding');
		}
		isBusySyncingFunding.value = false;
	}

	function openNoFundingTransactionsFoundModal() {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				title: 'Error',
				info: 'No Funding Transactions Found',
				acceptText: 'OK',
			},
		});
	}

	const totalFunding = computed(() => {
		return fimenu.fundings.reduce((acc, curr) => acc + curr.amount, 0);
	});

	const totalFundingInvalidMessage = computed(() => {
		if (totalFunding.value !== fimenu.expectedFundingTotal) {
			return 'Funding total does not match expected funding total: ' + (fimenu.expectedFundingTotal ?? 'TBD');
		} else {
			return null;
		}
	});
</script>
<style scoped>
	.sync-funding-button {
		width: 126px;
		height: 40px;
	}
</style>
